import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';

const bookingStatusColor = { CONFIRMED: 'badge-green', PENDING: 'badge-yellow', CANCELLED: 'badge-red', COMPLETED: 'badge-blue' };
const paymentStatusColor = { PAID: 'badge-green', PARTIAL: 'badge-yellow', UNPAID: 'badge-gray' };

export default function BookingList() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');

  const load = useCallback(async () => {
    setLoading(true);
    const base = supabase.from('bookings')
      .select('*, clients(full_name), packages(package_name), cost_currency:cost_currency_id(code), sell_currency:sell_currency_id(code)')
      .order('created_at', { ascending: false });

    let results = [];
    if (q) {
      // Search booking ref + client name + ticket numbers (passengers JSONB)
      const [{ data: byRef }, { data: byTicket }] = await Promise.all([
        (() => {
          let qb = base;
          if (statusFilter === 'ACTIVE') qb = qb.neq('booking_status', 'CANCELLED');
          else if (statusFilter) qb = qb.eq('booking_status', statusFilter);
          return qb.or(`booking_ref.ilike.%${q}%,clients.full_name.ilike.%${q}%`);
        })(),
        (() => {
          let qb = supabase.from('bookings')
            .select('*, clients(full_name), packages(package_name), cost_currency:cost_currency_id(code), sell_currency:sell_currency_id(code)')
            .order('created_at', { ascending: false })
            .filter('passengers', 'not.is', null)
            .filter('passengers::text', 'ilike', `%${q}%`);
          if (statusFilter === 'ACTIVE') qb = qb.neq('booking_status', 'CANCELLED');
          else if (statusFilter) qb = qb.eq('booking_status', statusFilter);
          return qb;
        })(),
      ]);
      const seen = new Set();
      for (const b of [...(byRef || []), ...(byTicket || [])]) {
        if (!seen.has(b.id)) { seen.add(b.id); results.push(b); }
      }
    } else {
      let qb = base;
      if (statusFilter === 'ACTIVE') qb = qb.neq('booking_status', 'CANCELLED');
      else if (statusFilter) qb = qb.eq('booking_status', statusFilter);
      const { data } = await qb;
      results = data || [];
    }

    setBookings(results);
    setLoading(false);
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Bookings</div><div className="page-subtitle">All client bookings</div></div>
        <Link to="/bookings/new" className="btn btn-primary">+ New Booking</Link>
      </div>
      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <input className="form-input" placeholder="Booking ref, client, ticket no..." value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 300 }} />
          <div style={{ display: 'flex', gap: 6 }}>
            {[['ACTIVE', 'Active'], ['', 'All'], ['PENDING', 'Pending'], ['CONFIRMED', 'Confirmed'], ['COMPLETED', 'Completed'], ['CANCELLED', 'Cancelled']].map(([v, l]) => (
              <button key={v} className={`btn btn-sm ${statusFilter === v ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setStatusFilter(v)}>{l}</button>
            ))}
          </div>
        </div>
        {loading ? <div className="spinner" /> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Ref</th><th>Client</th><th>Package</th><th>Travel Date</th><th>Pax</th><th style={{ textAlign: 'right' }}>Cost</th><th style={{ textAlign: 'right' }}>Sell</th><th style={{ textAlign: 'right' }}>GP</th><th>Status</th><th>Payment</th><th></th></tr></thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--primary)' }}>{b.booking_ref}</td>
                    <td>{b.clients?.full_name || ''}</td>
                    <td style={{ fontSize: 12 }}>{b.packages?.package_name || ''}</td>
                    <td>{b.travel_date?.slice(0, 10) || ''}</td>
                    <td>{b.num_pax}</td>
                    <td style={{ textAlign: 'right' }} className="amount">{b.cost_currency?.code} {parseFloat(b.total_cost || 0).toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }} className="amount">{b.sell_currency?.code} {parseFloat(b.total_sell || 0).toLocaleString()}</td>
                    <td style={{ textAlign: 'right', color: parseFloat(b.gross_profit || 0) >= 0 ? 'var(--success)' : 'var(--danger)' }} className="amount">{parseFloat(b.gross_profit || 0).toLocaleString()}</td>
                    <td>{b.booking_status !== 'CONFIRMED' && <span className={`badge ${bookingStatusColor[b.booking_status] || 'badge-gray'}`}>{b.booking_status}</span>}</td>
                    <td><span className={`badge ${paymentStatusColor[b.payment_status] || 'badge-gray'}`}>{b.payment_status}</span></td>
                    <td><Link to={`/bookings/${b.id}`} className="btn btn-secondary btn-sm">View</Link></td>
                  </tr>
                ))}
                {bookings.length === 0 && <tr><td colSpan={11}><div className="empty-state"><div className="empty-state-title">No bookings yet</div></div></td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
